import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Wallet, TrendingDown, TrendingUp, RefreshCw, AlertTriangle, CheckCircle2, Save, Info } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const fetchAuth = async (path, opts = {}) => {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || localStorage.getItem('token') || '';
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = '';
    try { detail = await res.text(); } catch (_e) { /* ignore */ }
    throw new Error(`${res.status} ${detail.slice(0, 200)}`);
  }
  return res.json();
};

const Stat = ({ label, value, sub, tone = 'neutral', testid }) => {
  const colors = {
    positive: 'text-emerald-700 bg-emerald-50',
    negative: 'text-rose-700 bg-rose-50',
    warning: 'text-amber-700 bg-amber-50',
    neutral: 'text-gray-900 bg-gray-50',
  }[tone];
  return (
    <div className={`rounded-2xl p-5 ${colors}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-3xl font-bold mt-1 tabular-nums">{value}</div>
      {sub ? <div className="text-xs mt-1 opacity-70">{sub}</div> : null}
    </div>
  );
};

const PromoFundTab = () => {
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [config, setConfig] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        fetchAuth('/api/admin/financial/promo-fund'),
        fetchAuth('/api/admin/financial/promo-fund/transactions?limit=50'),
      ]);
      setSummary(s);
      setConfig({ ...s.config });
      setTxns(t.transactions || []);
    } catch (e) {
      toast({ title: 'Failed to load promo fund', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    if (!config) return;
    setSavingCfg(true);
    try {
      const body = {
        surge_skim_pct: Number(config.surge_skim_pct),
        cancellation_skim_pct: Number(config.cancellation_skim_pct),
        service_charge_tithe_pct: Number(config.service_charge_tithe_pct),
        low_balance_threshold_mwk: Number(config.low_balance_threshold_mwk),
      };
      await fetchAuth('/api/admin/financial/promo-fund/config', { method: 'PUT', body: JSON.stringify(body) });
      toast({ title: 'Config saved', description: 'New skim rates apply to subsequent rides.' });
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setSavingCfg(false);
    }
  };

  const fmtK = (n) => `K${Math.round(Number(n || 0)).toLocaleString()}`;

  if (loading || !summary) {
    return <Card className="p-8 text-center text-gray-500" data-testid="promo-fund-loading">Loading promo fund…</Card>;
  }

  return (
    <div className="space-y-6" data-testid="promo-fund-tab">
      {/* Hero card */}
      <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
              <Wallet className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-[20px] font-bold text-emerald-900" data-testid="promo-fund-title">
                Self-Funded Promo Reserve
              </h2>
              <p className="text-[13px] text-emerald-800/80 max-w-2xl mt-1">
                Promotions and referral discounts are paid <b>only</b> from this fund. The fund is fed
                automatically by skims from surge premium and cancellation fees. Operating profit is
                <b> never</b> debited.
              </p>
            </div>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="bg-white"
            data-testid="promo-fund-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </Card>

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          label="Reserve balance"
          value={fmtK(summary.balance_mwk)}
          tone={summary.low_balance ? 'warning' : 'positive'}
          sub={summary.promotions_active ? 'Promotions active' : 'Reserve depleted'}
          testid="promo-fund-balance"
        />
        <Stat
          label="Inflow (last 7d)"
          value={fmtK(summary.credits_last_7d)}
          tone="positive"
          sub="Surge & cancellation skims"
          testid="promo-fund-inflow"
        />
        <Stat
          label="Outflow (last 7d)"
          value={fmtK(summary.debits_last_7d)}
          tone="negative"
          sub="Promo / referral payouts"
          testid="promo-fund-outflow"
        />
        <Stat
          label="Status"
          value={summary.low_balance ? 'Low balance' : 'Healthy'}
          tone={summary.low_balance ? 'warning' : 'positive'}
          sub={summary.low_balance ? `Below K${Number(summary.config?.low_balance_threshold_mwk || 0).toLocaleString()}` : 'Above warning threshold'}
          testid="promo-fund-status"
        />
      </div>

      {summary.low_balance && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="promo-fund-low-warning">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-[13px] text-amber-900">
            <b>Reserve is low.</b> Discounts will continue to apply only while the reserve covers each
            payout. New referral / promo redemptions are auto-skipped if the reserve cannot cover them —
            riders see a friendly toast instead.
          </div>
        </div>
      )}

      {/* Config */}
      {config && (
        <Card className="p-5" data-testid="promo-fund-config">
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-4 h-4 text-gray-500" />
            <h3 className="font-bold text-gray-900">Skim configuration</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { key: 'surge_skim_pct', label: 'Surge skim %', hint: '% of surge premium captured', max: 80 },
              { key: 'cancellation_skim_pct', label: 'Cancellation skim %', hint: '% of cancellation fees captured', max: 80 },
              { key: 'service_charge_tithe_pct', label: 'Service-charge tithe %', hint: 'OFF by default (keep 0 to honour 100% service-charge profit)', max: 20 },
              { key: 'low_balance_threshold_mwk', label: 'Low-balance threshold (K)', hint: 'Warn admin below this balance', max: 1000000 },
            ].map((f) => (
              <label key={f.key} className="block">
                <div className="text-[12px] font-semibold text-gray-700">{f.label}</div>
                <input
                  type="number"
                  min={0}
                  max={f.max}
                  step={f.key.includes('threshold') ? 100 : 1}
                  value={config[f.key] ?? 0}
                  onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 tabular-nums"
                  data-testid={`promo-fund-cfg-${f.key}`}
                />
                <div className="text-[11px] text-gray-500 mt-1">{f.hint}</div>
              </label>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button
              onClick={saveConfig}
              disabled={savingCfg}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="promo-fund-save-cfg"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {savingCfg ? 'Saving…' : 'Save configuration'}
            </Button>
            <span className="text-[11px] text-gray-500">Super admin only.</span>
          </div>
        </Card>
      )}

      {/* Ledger */}
      <Card className="p-0 overflow-hidden" data-testid="promo-fund-ledger">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Recent ledger</h3>
          <span className="text-[12px] text-gray-500 tabular-nums">{txns.length} entries</span>
        </div>
        {txns.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm" data-testid="promo-fund-empty">
            No transactions yet. As surge rides and cancellations happen, this list will populate.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {txns.map((t) => {
              const inflow = t.amount > 0;
              return (
                <div key={t.id} className="flex items-center gap-3 px-5 py-3" data-testid={`promo-fund-row-${t.id}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${inflow ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    {inflow ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-gray-900 truncate">
                      {t.source.replace(/_/g, ' ')}
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {t.ride_id ? `Ride ${String(t.ride_id).slice(0, 8)}…` : '—'} · {new Date(t.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className={`text-[15px] font-bold tabular-nums ${inflow ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {inflow ? '+' : ''}{fmtK(t.amount)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4 flex items-start gap-3" data-testid="promo-fund-policy">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="text-[12px] text-gray-700">
          <b>Profit policy.</b> Yachangu always retains 100% of the service charge per ride. Drivers
          always receive their full computed earnings. Promotions are paid <i>only</i> from the
          self-funded reserve above. If the reserve cannot cover a discount, the discount is
          auto-skipped at booking time.
        </div>
      </div>
    </div>
  );
};

export default PromoFundTab;
